/** biome-ignore-all lint/suspicious/noExplicitAny: `any` is required for the `AnyFunction` generic type - `unknown` breaks function parameter and return-type assignability, which is fundamental to how these stable callback wrappers operate */

import type { DebouncedFunc } from 'lodash';
import { debounce, throttle } from 'lodash';
import { useInsertionEffect, useRef } from 'react';

type AnyFunction = (...args: any[]) => any;

const useEventPreMountCallbackError = () => {
  throw new Error('useEvent should not be called before the component is mounted');
};

/**
 * Creates a stable function that will always contain the latest state or property values and doesn't require the use of
 * a dependency array. This is currently submitted as a React RFC and is explained here
 * https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md.
 *
 * @template F - The function type being wrapped
 * @param callback - The actual function to run.
 * @returns A stable reference to a function that always calls the latest callback
 */
const useEvent = <F extends AnyFunction>(callback: F): F => {
  // maintains the actual code to run...this is updated on every render
  const latestCallback = useRef<F>(useEventPreMountCallbackError as unknown as F);
  useInsertionEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  // creates the stable function that does not change on each render
  const stableCallback = useRef<F | null>(null);
  if (!stableCallback.current) {
    stableCallback.current = ((...args: Parameters<F>): ReturnType<F> => {
      return latestCallback.current(...args);
    }) as F;
  }

  return stableCallback.current;
};

type WrappedFunction<T extends AnyFunction> = (...args: Parameters<T>) => ReturnType<T>;

/**
 * Copied from the lodash types.
 */
interface ThrottleSettings {
  leading?: boolean | undefined;
  trailing?: boolean | undefined;
}

/**
 * Creates a throttled stable function that will always contain the latest state or property values.
 *
 * @template F - The function type being wrapped
 * @param callback - The actual function to run.
 * @param wait - The number of milliseconds to throttle invocations to.
 * @param options - Throttle options (leading/trailing edge execution).
 * @returns A throttled stable reference to a function that always calls the latest callback
 */
const useEventThrottle = <F extends AnyFunction>(
  callback: F,
  wait?: number,
  options?: ThrottleSettings
): DebouncedFunc<F> => {
  const latestCallback = useRef<F | undefined>(undefined);
  useInsertionEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  // creates the stable function that does not change on each render
  const stableCallback = useRef<DebouncedFunc<F> | null>(null);
  if (!stableCallback.current) {
    stableCallback.current = throttle(
      ((...args: Parameters<F>): ReturnType<F> => {
        return latestCallback.current!(...args);
      }) as F,
      wait,
      options
    );
  }

  return stableCallback.current;
};

/**
 * Copied from lodash types.
 */
interface DebounceSettings {
  leading?: boolean | undefined;
  maxWait?: number | undefined;
  trailing?: boolean | undefined;
}

/**
 * Creates a debounced stable function that will always contain the latest state or property values.
 *
 * @template F - The function type being wrapped
 * @param callback - The actual function to run.
 * @param wait - The number of milliseconds to delay invocations.
 * @param options - Debounce options (leading/trailing edge execution, maxWait).
 * @returns A debounced stable reference to a function that always calls the latest callback
 */
const useEventDebounce = <F extends AnyFunction>(
  callback: F,
  wait?: number,
  options?: DebounceSettings
): DebouncedFunc<F> => {
  const latestCallback = useRef<F | undefined>(undefined);
  useInsertionEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  // creates the stable function that does not change on each render
  const stableCallback = useRef<DebouncedFunc<F> | null>(null);
  if (!stableCallback.current) {
    stableCallback.current = debounce(
      ((...args: Parameters<F>): ReturnType<F> => {
        return latestCallback.current!(...args);
      }) as F,
      wait,
      options
    );
  }

  return stableCallback.current;
};

/**
 * Creates a stable function that will always contain the latest state or property values and doesn't require the use of
 * a dependency array. This is currently submitted as a React RFC and is explained here
 * https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md.
 *
 * This variant provides stricter typing for wrapped functions.
 *
 * @template F - The function type being wrapped
 * @param callback - The actual function to run.
 * @returns A stable reference to a function that always calls the latest callback
 */
const useEventType = <F extends WrappedFunction<F>>(callback: F): F => {
  const latestCallback = useRef<F>(useEventPreMountCallbackError as unknown as F);
  useInsertionEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  const stableCallback = useRef<F | null>(null);
  if (!stableCallback.current) {
    stableCallback.current = ((...args: Parameters<F>): ReturnType<F> => {
      return latestCallback.current(...args);
    }) as F;
  }

  return stableCallback.current;
};

export { useEvent, useEventThrottle, useEventDebounce, useEventType };
